import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { configApi } from '../api/smartTokenizerApi';
import {
  explorerAddressUrl,
  explorerTokenUrl,
  explorerTransactionUrl
} from '../config/coreBlockchainExplorer';

const ClientConfigContext = createContext(null);

export function ClientConfigProvider({ children }) {
  const [blockExplorerBaseUrl, setBlockExplorerBaseUrl] = useState('');
  const [ipfsGatewayBaseUrl, setIpfsGatewayBaseUrl] = useState('');
  const [skipIpfsUploadsIfWriteUnavailable, setSkipIpfsUploadsIfWriteUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await configApi.getClient();
        const u = data?.blockExplorerBaseUrl ?? data?.BlockExplorerBaseUrl ?? '';
        const g = data?.ipfsGatewayBaseUrl ?? data?.IpfsGatewayBaseUrl ?? '';
        const skip = Boolean(
          data?.skipIpfsUploadsIfWriteUnavailable ?? data?.SkipIpfsUploadsIfWriteUnavailable
        );
        if (!cancelled) {
          setBlockExplorerBaseUrl(String(u || '').trim());
          setIpfsGatewayBaseUrl(String(g || '').trim().replace(/\/+$/, ''));
          setSkipIpfsUploadsIfWriteUnavailable(skip);
        }
      } catch {
        if (!cancelled) {
          setBlockExplorerBaseUrl('');
          setIpfsGatewayBaseUrl('');
          setSkipIpfsUploadsIfWriteUnavailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const base = blockExplorerBaseUrl;
    const ipfsBase = ipfsGatewayBaseUrl;
    return {
      blockExplorerBaseUrl: base,
      ipfsGatewayBaseUrl: ipfsBase,
      ipfsGatewayConfigured: Boolean(ipfsBase),
      skipIpfsUploadsIfWriteUnavailable,
      /** Public IPFS URL for a CID: `https://ipf.sk/{cid}` */
      ipfsPublicUrl: (cid) => {
        const c = String(cid || '').trim();
        if (!c || !ipfsBase) return '';
        if (c.toLowerCase().startsWith('local-')) return '';
        return `${ipfsBase}/${c}`;
      },
      explorerConfigured: Boolean(base),
      addressExplorerUrl: (addr) => explorerAddressUrl(base, addr),
      tokenExplorerUrl: (contract, tokenId) => explorerTokenUrl(base, contract, tokenId),
      transactionExplorerUrl: (txHash) => explorerTransactionUrl(base, txHash)
    };
  }, [blockExplorerBaseUrl, ipfsGatewayBaseUrl, skipIpfsUploadsIfWriteUnavailable]);

  return <ClientConfigContext.Provider value={value}>{children}</ClientConfigContext.Provider>;
}

export function useClientConfig() {
  const ctx = useContext(ClientConfigContext);
  if (!ctx) {
    throw new Error('useClientConfig must be used within ClientConfigProvider');
  }
  return ctx;
}
